import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Container, Section } from 'components/shared';
import cx from 'classnames';
import { Button } from 'components/components/atoms';
import styled, { css, keyframes } from 'styled-components';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { Helmet } from '@dr.pogodin/react-helmet';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import breakpoints from 'utils/breakpoints';
import { hasPrice, parsePrice } from 'utils/parsePrice';
import { useCountryValue } from 'stores/country';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import { scrollToSection } from 'utils/scrollTo';
import { useLocation } from 'react-router-dom';
import useFavorites from 'utils/hooks/useFavorites';
import HomeTypeSections, {
  HOME_TYPES,
  HOME_TYPE_OPTIONS,
  homeTypeFromHash,
} from 'components/components/molecules/HomeTypeSections';

import loadable from '@loadable/component';
const ContentBlock = loadable(() =>
  import('components/components/molecules/ContentBlock'),
);
const QMICard = loadable(() =>
  import('components/components/molecules/QMICard'),
);
const QMICardMobile = loadable(() =>
  import('components/components/molecules/QMICardMobile'),
);

const DEFAULT_HOME_TYPE = HOME_TYPES.QMI;
const MOBILE_CARDS_STEP = 4;

const swipeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(1.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Content slides up on every tab change. Remounted by key rather than
 * toggling a class.
 */
const SwipeUp = styled.div`
  animation: ${swipeUp} 0.45s ease-out both;
`;

const TextColumn = styled.div`
  ${({ $animate }) =>
    $animate &&
    css`
      && * {
        animation: none;
      }
    `}
`;

const StyledSection = styled(Section)`
  padding-bottom: 1.25rem !important;
`;

const StyledContainer = styled(Container)`
  ${({ $showHomeTypes }) =>
    $showHomeTypes ? 'min-height: 17rem; ' : 'min-height: 8rem; '}

  @media ${breakpoints.md} {
    min-height: 31.25rem;
  }
`;

const getSlides = (
  cards,
  onFocusChange,
  country,
  newSchemaColorHexaCode = '',
  newSchemaTextColorHexaCode = '',
  showNewSchemaColor = false,
  desktopVisibleCards,
  priceDifferentialText,
  favoritesList,
  handleFavoriteClick,
  itemRefShowMore,
) => {
  return cards
    .sort((a, b) => {
      const aPrice =
        a.price && hasPrice(a.price.price) ? parsePrice(a.price.price) : null;
      const bPrice =
        b.price && hasPrice(b.price.price) ? parsePrice(b.price.price) : null;

      if (aPrice !== null && bPrice !== null) {
        return aPrice - bPrice;
      }

      if (aPrice === null) {
        return bPrice === null
          ? a.title.toLowerCase().localeCompare(b.title.toLowerCase())
          : 1;
      }
      return -1;
    })
    .reduce((accum, c, i) => {
      accum.push(
        <div
          tabIndex={-1}
          ref={(e) => (itemRefShowMore.current[i] = e)}
          key={c.id}
          className={
            'md:pb-16 md:pt-4 p-2 md:pr-0' +
            (i < desktopVisibleCards ? '' : ' hidden')
          }
        >
          <span>&nbsp;</span>
          <QMICard
            data-aos='fade-left'
            data-aos-delay={i * 150}
            className='bg-light-blue w-full rounded'
            card={c}
            country={country}
            newSchemaColorHexaCode={newSchemaColorHexaCode}
            newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
            showNewSchemaColor={showNewSchemaColor}
            priceDifferentialText={priceDifferentialText}
            favoritesList={favoritesList}
            handleFavoriteClick={handleFavoriteClick}
          />
        </div>,
      );
      return accum;
    }, []);
};

const RecentlyViewed = ({ fields, rendering }) => {
  const { componentName } = rendering;
  const [{ country }] = useCountryValue();
  const {
    newSchemaColorHexaCode,
    newSchemaTextColorHexaCode,
    showNewSchemaColor,
  } = useNewColorSchemaOptions();
  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();

  const targetRefFontSize = useRef();
  const [dynamicClass, setDynamicClass] = useState('lg:grid-cols-2');
  const [position, setPosition] = useState(0);
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const isLandscape = ['xl'].includes(breakpoint);
  const isLandscapeXl = ['2xl'].includes(breakpoint);

  const [mobileVisibleCards, setMobileVisibleCards] =
    useState(MOBILE_CARDS_STEP);
  const [homeType, setHomeType] = useState(DEFAULT_HOME_TYPE);

  // Derived every render, never captured in a useState initializer:
  // useBreakpoint() returns SSR_BREAKPOINT (xs) on the first pass, so an
  // initializer would freeze the desktop grid at the mobile page size.
  const desktopPageSize = isLandscapeXl ? 6 : isLandscape ? 4 : 2;
  const desktopPageStep = isLandscapeXl ? 3 : isLandscape ? 2 : 1;
  const [desktopExtraPages, setDesktopExtraPages] = useState(0);
  const desktopVisibleCards =
    desktopPageSize + desktopExtraPages * desktopPageStep;

  const itemRefShowMore = useRef([]);
  const [lastDesktopVisibleCards, setLastDesktopVisibleCards] = useState([]);

  const priceDifferentialText =
    country === 'USA'
      ? getFieldValue(fields, 'priceDifferentialTextUSA')
      : getFieldValue(fields, 'priceDifferentialTextCAN');

  const handleFavoriteClick = (id) => {
    const isFavorite = favoritesList.includes(id);
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  // `result` always arrives: the single collection this block used to resolve,
  // typed by `cardsType`. `qmiResult` / `planResult` are the new shape, each
  // with its own headline, copy, cta and cards. The tab header only shows when
  // both are there - with one collection there is nothing to choose.
  const legacyResult = getFieldValue(fields, 'result') || {};
  const qmiResult = getFieldValue(fields, 'qmiResult');
  const planResult = getFieldValue(fields, 'planResult');
  const showHomeTypes = !!(qmiResult && planResult);

  const resultsByType = {
    [HOME_TYPES.QMI]: qmiResult || {},
    [HOME_TYPES.PLAN]: planResult || {},
  };

  // The active tab drives the whole block: heading, copy, CTA and cards all
  // come from the same result.
  const result = showHomeTypes ? resultsByType[homeType] : legacyResult;
  const activeCards = result.cards || [];

  // Feeds each tab badge and the gray of an empty one. Zero without tabs.
  const cardCounts = {
    [HOME_TYPES.QMI]: (resultsByType[HOME_TYPES.QMI].cards || []).length,
    [HOME_TYPES.PLAN]: (resultsByType[HOME_TYPES.PLAN].cards || []).length,
  };

  // Without tabs the field decides, not the state: `result` may hold plans.
  // cardsType is a bare string, not { value }, so getFieldValue misses it.
  const cardsTypeField = fields && fields.cardsType;
  const activeType = showHomeTypes
    ? homeType
    : (cardsTypeField && cardsTypeField.value) ||
      cardsTypeField ||
      HOME_TYPES.QMI;

  // With tabs every collection renders, the inactive one hidden, so the
  // server HTML carries both lists and not just the selected one. Without
  // tabs there is the one `result`.
  const panelTypes = showHomeTypes
    ? HOME_TYPE_OPTIONS.map((o) => o.value)
    : [activeType];
  const isActivePanel = (type) => !showHomeTypes || type === homeType;
  const resultOf = (type) =>
    showHomeTypes ? resultsByType[type] : legacyResult;

  // A hidden panel sits at the first block of results: that is where a tab
  // change rewinds to, so showing it needs no re-render of its cards.
  const desktopVisibleOf = (type) =>
    isActivePanel(type) ? desktopVisibleCards : desktopPageSize;
  const mobileVisibleOf = (type) =>
    isActivePanel(type) ? mobileVisibleCards : MOBILE_CARDS_STEP;

  // One noun for the count and the screen reader, so they cannot drift apart.
  const nounOf = (type) =>
    type === HOME_TYPES.PLAN ? 'floorplans' : 'quick move-in homes';

  const showingTextOf = (type) => {
    const cards = resultOf(type).cards || [];
    const visibleCards = isMobile
      ? mobileVisibleOf(type)
      : desktopVisibleOf(type);
    return cards.length
      ? `Showing ${Math.min(visibleCards, cards.length)} of ${
          cards.length
        } ${nounOf(type)}`
      : `No ${nounOf(type)} available`;
  };

  // Escape hatch: offer the other collection instead of a dead end.
  const otherHomeTypeOf = (type) =>
    type === HOME_TYPES.PLAN ? HOME_TYPES.QMI : HOME_TYPES.PLAN;
  const otherTypeCtaOf = (type) =>
    otherHomeTypeOf(type) === HOME_TYPES.PLAN
      ? 'SEE FLOORPLANS'
      : 'SEE QUICK MOVE-IN HOMES';

  // Switching tabs rewinds the grid to the first block of results.
  const handleHomeTypeChange = (nextType) => {
    if (nextType === homeType) return;
    setHomeType(nextType);
    setDesktopExtraPages(0);
    setMobileVisibleCards(MOBILE_CARDS_STEP);
    setLastDesktopVisibleCards([]);

    // Keeps the URL shareable. replaceState and not location.hash: no scroll
    // jump, no history entry per click, and the router never sees it, so the
    // effect above does not re-run.
    const option = HOME_TYPE_OPTIONS.find((o) => o.value === nextType);
    if (option && typeof window !== 'undefined' && window.history) {
      // Full path, not just the hash: the document has <base href="/">, so a
      // bare `#floorplans` resolves against the base and drops the community.
      const { pathname, search } = window.location;
      window.history.replaceState(
        null,
        '',
        `${pathname}${search}#${option.anchor}`,
      );
    }
  };

  // On mobile the CTA leaves ContentBlock so it can head the list, with the
  // same style it painted there: filled mattamy-blue.
  const renderMobileCta = (panelResult, className) =>
    panelResult.cta && panelResult.cta.text ? (
      <Button
        className={cx('px-16', className)}
        showNewSchemaColor={showNewSchemaColor}
        color={showNewSchemaColor ? newSchemaColorHexaCode : 'mattamy-blue'}
        textColor={showNewSchemaColor ? newSchemaTextColorHexaCode : 'white'}
        variant='filled'
        href={panelResult.cta.href}
        target={panelResult.cta.target}
        linkWrapperDescription={panelResult.cta.text}
        buttonDescription={panelResult.cta.text}
      >
        {panelResult.cta.text}
      </Button>
    ) : null;

  // Same panel in both views; only the padding changes.
  const renderEmptyState = (type) => (
    <div className='w-full flex flex-col items-center justify-center text-center px-6 py-12 md:px-8 md:py-16 border-2 border-dashed border-border-light-blue rounded'>
      <p className='font-trade-gothic-20 uppercase text-2xl text-mattamy-blue'>
        {`No ${nounOf(type)} yet`}
      </p>
      <p className='mt-2 font-graphie text-mattamy-gray-font'>
        {`This community doesn't have any ${nounOf(type)} to show right now.`}
      </p>
      {cardCounts[otherHomeTypeOf(type)] > 0 && (
        <Button
          className='mt-6'
          showNewSchemaColor={showNewSchemaColor}
          color={showNewSchemaColor ? newSchemaColorHexaCode : 'mattamy-blue'}
          textColor={
            showNewSchemaColor ? newSchemaTextColorHexaCode : 'mattamy-blue'
          }
          variant='outlined'
          buttonDescription={otherTypeCtaOf(type)}
          onClick={() => handleHomeTypeChange(otherHomeTypeOf(type))}
        >
          <p className='px-6'>{otherTypeCtaOf(type)}</p>
        </Button>
      )}
    </div>
  );

  // With tabs both ItemLists ship: each has its own @id and both collections
  // are reachable from this page. Without tabs, the usual single one.
  const cardsSchemas = (
    showHomeTypes
      ? [
          getFieldValue(fields, 'qmiCardsSchema'),
          getFieldValue(fields, 'planCardsSchema'),
        ]
      : [getFieldValue(fields, 'cardsSchema')]
  ).filter(Boolean);

  const onFocusChange = useCallback(
    (index) => {
      if (index < slides.length - 2) {
        setPosition(index);
      }
    },
    [position],
  );

  // efc card-size
  useEffect(() => {
    // Focus  ...rest of state update
    const lastLen = lastDesktopVisibleCards[lastDesktopVisibleCards.length - 1];
    if (itemRefShowMore.current[lastLen]) {
      itemRefShowMore.current[lastLen].focus();
    }
  });

  useEffect(() => {
    const observer = new ResizeObserver(checkFontSizeChange);
    if (targetRefFontSize && targetRefFontSize.current) {
      observer.observe(targetRefFontSize.current);
    }
    return () => {
      observer.disconnect();
    };
    // The observed node remounts on tab change (the animation) and across the
    // breakpoint; without these deps the observer watches a detached node.
  }, [homeType, isMobile]);
  const checkFontSizeChange = () => {
    if (targetRefFontSize.current) {
      const fontSize = parseInt(
        getComputedStyle(targetRefFontSize.current).fontSize,
        10,
      );
      if (fontSize > 20) {
        setDynamicClass('lg:grid-cols-1');
      } else {
        setDynamicClass('lg:grid-cols-2');
      }
    }
  };

  // Derived, not state: with state the render after a tab change painted the
  // previous tab cards for one frame, right as the animation started.
  // Only the active panel registers in itemRefShowMore: SHOW MORE focuses by
  // index into it, and a hidden card cannot take focus.
  const slidesByType = panelTypes.reduce((accum, type) => {
    accum[type] = getSlides(
      resultOf(type).cards || [],
      onFocusChange,
      country,
      newSchemaColorHexaCode,
      newSchemaTextColorHexaCode,
      showNewSchemaColor,
      desktopVisibleOf(type),
      priceDifferentialText,
      favoritesList,
      handleFavoriteClick,
      isActivePanel(type) ? itemRefShowMore : { current: [] },
    );
    return accum;
  }, {});
  const slides = slidesByType[showHomeTypes ? homeType : activeType] || [];

  const blueBackground = getFieldValue(fields, 'blueBackground');

  const showContent = isMobile || position === 0;

  const location = useLocation();
  const hash = location.hash;
  // Deep links: #availablehomes scrolls here; #quickmoveins / #floorplans also
  // open the collection they name. Without the header those anchors are not in
  // the DOM, there is no tab to select and the page stays at the top.
  useEffect(() => {
    const anchor = hash ? hash.substring(1).toLowerCase() : '';
    const hashHomeType = showHomeTypes ? homeTypeFromHash(anchor) : undefined;
    if (hashHomeType) setHomeType(hashHomeType);

    const scrollTarget =
      hashHomeType || anchor === 'availablehomes' ? anchor : '';
    if (!scrollTarget) return undefined;

    // 2s is inherited: the section keeps growing after first paint.
    const timer = setTimeout(() => scrollToSection(scrollTarget, 80), 2000);
    return () => clearTimeout(timer);
    // This effect used to declare no deps and leaked a setTimeout per render.
  }, [hash, showHomeTypes]);

  // With tabs the guard looks at both collections: going by the active one
  // would unmount the block on an empty tab, with no way back.
  const hasAnyCard = showHomeTypes
    ? cardCounts[HOME_TYPES.QMI] + cardCounts[HOME_TYPES.PLAN] > 0
    : activeCards.length > 0;
  if (!hasAnyCard) return null;

  return (
    <StyledSection
      className={cx({
        'bg-light-blue': blueBackground,
        'bg-white': !blueBackground,
      })}
      id='availablehomes'
    >
      {cardsSchemas.length > 0 && (
        <Helmet>
          {cardsSchemas.map((schema) => (
            <script
              key={schema['@id'] || schema.name}
              defer
              type='application/ld+json'
            >
              {JSON.stringify(schema)}
            </script>
          ))}
        </Helmet>
      )}
      <StyledContainer
        className='w-full h-full relative flex flex-col items-stretch'
        $showHomeTypes={showHomeTypes}
      >
        {/* items-stretch, not items-center: the header spans the full width. */}
        {showHomeTypes && (
          <HomeTypeSections
            className='mb-8'
            value={homeType}
            counts={cardCounts}
            onChange={handleHomeTypeChange}
            isMobile={isMobile}
          />
        )}

        {panelTypes.map((type) => {
          const isActive = isActivePanel(type);
          const panelResult = resultOf(type);
          const panelCards = panelResult.cards || [];
          const panelVisibleCards = desktopVisibleOf(type);

          return (
            <SwipeUp
              // One panel per collection, always mounted; the inactive one is
              // display:none. Coming back from display:none replays the
              // animation, so tab changes still swipe up.
              key={type}
              $animate={showHomeTypes}
              className={cx(
                'w-full h-full relative md:flex-row justify-between',
                isActive ? 'flex' : 'hidden',
              )}
            >
              <TextColumn
                $animate={showHomeTypes}
                className='w-full md:w-5/12 lg:w-1/4 relative'
              >
                <ContentBlock
                  textColor={'blue'}
                  heading={panelResult.headline}
                  copy={panelResult.copy}
                  // On mobile this section paints the CTA, not ContentBlock.
                  ctas={isMobile ? [] : [panelResult.cta]}
                  ButtonProps={{
                    color: 'mattamy-blue',
                    className: 'px-16',
                    variant: isMobile ? 'filled' : 'outlined',
                  }}
                  cutOffTitle={false}
                  newSchemaColorHexaCode={newSchemaColorHexaCode}
                  newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
                  showNewSchemaColor={showNewSchemaColor}
                  isMobile={isMobile}
                />
              </TextColumn>

              {!isMobile && (
                <div
                  className={cx(
                    'ml-16 relative w-full h-full flex flex-col space-between -mt-4 z-10',
                  )}
                >
                  {/* Always in the DOM: mounted alongside the change it would
                      not be announced. */}
                  <p className='sr-only' aria-live='polite'>
                    {showingTextOf(type)}
                  </p>

                  <div
                    ref={isActive ? targetRefFontSize : undefined}
                    className={`w-full mt-8 md:mt-0 grid grid-cols-1 ${dynamicClass} 2xl:grid-cols-3 gap-4`}
                  >
                    {slidesByType[type]}
                  </div>

                  {panelCards.length < 1 && renderEmptyState(type)}
                  <div className='w-full mt-0 text-center'>
                    <Button
                      className={cx('mt-4', {
                        // "focus:bg-mattamy-blue-dark focus:text-white": !showNewSchemaColor,
                        hidden: !(panelCards.length > panelVisibleCards),
                      })}
                      showNewSchemaColor={showNewSchemaColor}
                      color={
                        showNewSchemaColor
                          ? newSchemaColorHexaCode
                          : 'mattamy-blue'
                      }
                      textColor={
                        showNewSchemaColor
                          ? newSchemaTextColorHexaCode
                          : 'white'
                      }
                      variant='filled'
                      buttonDescription={'SHOW MORE'}
                      onClick={() => {
                        if (panelCards.length > panelVisibleCards) {
                          setDesktopExtraPages((n) => n + 1);
                        }

                        setLastDesktopVisibleCards((prvs) => [
                          ...prvs,
                          panelVisibleCards,
                        ]);
                      }}
                    >
                      <p className='w-40'>{'SHOW MORE'}</p>
                    </Button>
                  </div>
                </div>
              )}
            </SwipeUp>
          );
        })}
      </StyledContainer>

      {isMobile && (
        <div
          ref={targetRefFontSize}
          className={cx(
            'relative w-full h-full transition-all duration-200 flex space-between -mt-4',
          )}
        >
          {panelTypes.map((type) => {
            const panelResult = resultOf(type);
            const panelCards = panelResult.cards || [];
            const panelVisibleCards = mobileVisibleOf(type);

            return (
              <SwipeUp
                key={type}
                $animate={showHomeTypes}
                className={cx('w-full md:mt-0 mx-4', {
                  hidden: !isActivePanel(type),
                })}
              >
                {panelCards.length < 1 && renderEmptyState(type)}

                {panelCards.map((sc, index) => (
                  <QMICardMobile
                    key={sc.id}
                    {...sc}
                    country={country}
                    index={index}
                    mobileVisibleCards={panelVisibleCards}
                    newSchemaColorHexaCode={newSchemaColorHexaCode}
                    newSchemaTextColorHexaCode={'#000'}
                    showNewSchemaColor={showNewSchemaColor}
                    priceDifferentialText={priceDifferentialText}
                    favoritesList={favoritesList}
                  />
                ))}

                <Button
                  className={cx('mt-4', {
                    hidden: !(panelCards.length > panelVisibleCards),
                  })}
                  showNewSchemaColor={showNewSchemaColor}
                  color={
                    showNewSchemaColor ? newSchemaColorHexaCode : 'mattamy-blue'
                  }
                  textColor={
                    showNewSchemaColor ? newSchemaTextColorHexaCode : 'white'
                  }
                  variant='filled'
                  buttonDescription={'SHOW MORE'}
                  onClick={() =>
                    setMobileVisibleCards((n) => n + MOBILE_CARDS_STEP)
                  }
                >
                  <p className='w-40'>{'SHOW MORE'}</p>
                </Button>
                <div className='w-full mt-4'>
                  {renderMobileCta(panelResult, 'mb-6 w-full')}
                  {/* No count on mobile, but tab changes and SHOW MORE stay
                      audible. */}
                  <p className='sr-only' aria-live='polite'>
                    {showingTextOf(type)}
                  </p>
                </div>
              </SwipeUp>
            );
          })}
        </div>
      )}
    </StyledSection>
  );
};

export default RecentlyViewed;
